import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaDownload, FaEye } from 'react-icons/fa';

interface HeaderProps {
  childName: string;
  reportDate: string;
  childAge?: string;
  childDateOfBirth?: string;
  childGender?: string;
  childInsuranceStatus?: string;
  viewCount?: number;
  accessCount?: number;
  onViewReport?: () => void;
  canDownloadReport?: boolean;
  onDownloadReport?: () => void;
  renderBelowNav?: React.ReactNode;
  showProviderNote?: boolean;
  showCaregiverDetails?: boolean;
}

const getViewCount = (viewCount?: number, fallbackCount?: number) => {
  const total = typeof viewCount === 'number' ? viewCount : fallbackCount;
  return typeof total === 'number' ? total : 0;
};

const Header: React.FC<HeaderProps> = ({
  viewCount,
  accessCount,
  onViewReport,
  canDownloadReport = false,
  onDownloadReport,
  renderBelowNav,
  showProviderNote = false,
  showCaregiverDetails = true,
}) => {
  const { t, i18n } = useTranslation();
  const accessSummary = t('header.views', { count: getViewCount(viewCount, accessCount) });
  const isSpanish = i18n.language?.startsWith('es');
  const nextLanguage = isSpanish ? 'en' : 'es';
  const languageActionLabel = isSpanish ? 'English' : 'Español';
  const sectionLinks = useMemo(() => [
    { id: 'report-overview', label: t('sectionNav.overview') },
    ...(showCaregiverDetails ? [{ id: 'caregiver-details', label: t('sectionNav.caregiverDetails') }] : []),
    ...(showProviderNote ? [{ id: 'provider-note', label: t('sectionNav.providerNote') }] : []),
    { id: 'provider-resources', label: t('sectionNav.providerResources') },
    { id: 'support', label: t('sectionNav.support') },
    { id: 'mobile-app', label: t('sectionNav.mobileApp') },
  ], [showCaregiverDetails, showProviderNote, t]);
  const [activeSection, setActiveSection] = useState(sectionLinks[0]?.id ?? 'report-overview');

  useEffect(() => {
    const topbar = document.querySelector<HTMLElement>('.report-topbar');
    if (!topbar) return;

    const updateTopbarHeight = () => {
      document.documentElement.style.setProperty('--topbar-height', `${Math.ceil(topbar.getBoundingClientRect().height)}px`);
    };

    updateTopbarHeight();
    const observer = new ResizeObserver(updateTopbarHeight);
    observer.observe(topbar);

    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty('--topbar-height');
    };
  }, []);

  useEffect(() => {
    let frame = 0;

    const updateActiveSection = () => {
      frame = 0;
      const stickyHeader = document.querySelector<HTMLElement>('.report-topbar');
      const stickyTimeline = document.querySelector<HTMLElement>('.checkin-timeline');
      const headerOffset = (stickyHeader?.getBoundingClientRect().height ?? 0)
        + (stickyTimeline?.getBoundingClientRect().height ?? 0)
        + 24;
      const sections = sectionLinks
        .map(({ id }) => {
          const element = document.getElementById(id);
          return element ? { id, rect: element.getBoundingClientRect() } : null;
        })
        .filter((section): section is { id: string; rect: DOMRect } => Boolean(section));
      const visibleSections = sections.filter(({ rect }) => (
        rect.bottom > headerOffset && rect.top < window.innerHeight
      ));
      const closestVisibleSection = visibleSections.reduce<{
        id: string;
        rect: DOMRect;
      } | null>((closest, section) => {
        if (!closest) return section;

        return Math.abs(section.rect.top - headerOffset) < Math.abs(closest.rect.top - headerOffset)
          ? section
          : closest;
      }, null);
      const mostRecentSectionAboveHeader = sections
        .filter(({ rect }) => rect.top <= headerOffset)
        .sort((first, second) => second.rect.top - first.rect.top)[0];
      const current = closestVisibleSection?.id
        ?? mostRecentSectionAboveHeader?.id
        ?? sectionLinks[0]?.id
        ?? 'report-overview';

      setActiveSection(current);
    };

    const queueUpdate = () => {
      if (!frame) frame = window.requestAnimationFrame(updateActiveSection);
    };

    updateActiveSection();
    window.addEventListener('scroll', queueUpdate, { passive: true });
    window.addEventListener('resize', queueUpdate);

    return () => {
      window.removeEventListener('scroll', queueUpdate);
      window.removeEventListener('resize', queueUpdate);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [sectionLinks]);

  const handleSectionNavigation = (event: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    const section = document.getElementById(id);
    if (!section) return;

    event.preventDefault();
    const stickyHeader = document.querySelector<HTMLElement>('.report-topbar');
    const stickyTimeline = document.querySelector<HTMLElement>('.checkin-timeline');
    const headerOffset = (stickyHeader?.getBoundingClientRect().height ?? 0)
      + (stickyTimeline?.getBoundingClientRect().height ?? 0)
      + 12;
    const top = Math.max(0, window.scrollY + section.getBoundingClientRect().top - headerOffset);

    setActiveSection(id);
    window.history.replaceState(null, '', `#${id}`);
    window.scrollTo({ top, behavior: 'smooth' });
  };

  useEffect(() => {
    document.documentElement.lang = isSpanish ? 'es' : 'en';
  }, [isSpanish]);

  const handleToggleLanguage = async () => {
    window.dispatchEvent(
      new CustomEvent('mamabear:report-language-change', {
        detail: { language: nextLanguage },
      }),
    );
    await i18n.changeLanguage(nextLanguage);
    localStorage.setItem('reportLanguage', nextLanguage);
  };

  return (
    <>
      <header className="report-topbar">
        <div className="report-frame">
          <div className="report-topbar__inner">
            <a
              href="https://mamabearhealth.com/"
              target="_blank"
              rel="noreferrer"
              className="report-brand"
            >
              <span className="report-brand__mark" aria-hidden="true">
                <img
                  src="/asset/mamabear_round.png"
                  alt=""
                />
                {/* Previous bear logo retained in case the PI asks to restore it.
                <img src="/asset/mamabearicon.png" alt="" />
                */}
              </span>
              <span className="report-brand__copy">
                <span className="report-brand__eyebrow">{t('header.secureViewer')}</span>
                <span className="report-brand__name">MamaBear Health</span>
              </span>
            </a>

            <div className="report-header-controls">
              <div className="report-pill-row">
                <span className="report-pill">{accessSummary}</span>
                {onViewReport && (
                  <button
                    type="button"
                    className="report-button report-button--secondary report-button--compact"
                    onClick={onViewReport}
                    title={t('header.viewReport')}
                    aria-label={t('header.viewReport')}
                  >
                    <FaEye title={t('header.viewReport')} />
                    <span className="report-button__label--desktop">{t('header.viewReport')}</span>
                    <span className="report-button__label--mobile">{t('header.reportShort')}</span>
                  </button>
                )}
                {canDownloadReport && onDownloadReport && (
                  <button
                    type="button"
                    className="report-button report-button--primary report-button--compact"
                    onClick={onDownloadReport}
                    title={t('header.downloadReport')}
                    aria-label={t('header.downloadReport')}
                  >
                    <FaDownload title={t('header.downloadReport')} />
                    <span className="report-button__label--desktop">{t('header.downloadReport')}</span>
                    <span className="report-button__label--mobile">{t('header.downloadShort')}</span>
                  </button>
                )}
              </div>
              <button
                type="button"
                className="report-language-toggle report-web-only-control"
                onClick={handleToggleLanguage}
                title={t('header.languageLabel')}
                aria-label={t('header.languageLabel')}
              >
                <span>{languageActionLabel}</span>
              </button>
            </div>
          </div>

          <nav className="report-section-nav" aria-label={t('sectionNav.label')}>
            <span className="report-section-nav__label">{t('sectionNav.jumpTo')}</span>
            <div className="report-section-nav__links">
              {sectionLinks.map(({ id, label }) => (
                <a
                  key={id}
                  href={`#${id}`}
                  className={`report-section-nav__link ${activeSection === id ? 'report-section-nav__link--active' : ''}`}
                  aria-current={activeSection === id ? 'location' : undefined}
                  onClick={(event) => handleSectionNavigation(event, id)}
                >
                  {label}
                </a>
              ))}
            </div>
          </nav>
        </div>
      </header>

      {renderBelowNav}
    </>
  );
};

export default Header;
